import { useEffect, useRef, useState } from "react";
import { t } from "../../../features/store-language/i18n.ts";
import { agencySessionKey } from "../../../utils/agencySession.ts";
import { confirmAgencyContact, requestAgencyContact } from "../Auth/agencyIdentityService.ts";
import { normalizeLoginDigits } from "../Auth/customerIdentityService.ts";
import { formatAgencyOtpCountdown, parseAgencyOtpTiming, type AgencyOtpTiming } from "../Auth/agencyOtpTiming.ts";
import { AccountButton, AccountForm, AccountInput, useAccountAction } from "../../shared/account/AccountUI.tsx";
import AppFeedback from "../../shared/AppFeedback.tsx";

type ContactValues = { recipient: string; currentPassword: string; code?: string };
type Verification = AgencyOtpTiming & { id: string; recipient: string };

export default function AgencyContactForm({ isEmail, onConfirmed, onPendingChange }: {
  isEmail: boolean;
  onConfirmed: (recipient: string, isEmail: boolean) => void;
  onPendingChange: (pending: boolean) => void;
}) {
  const [form] = AccountForm.useForm<ContactValues>();
  const [verification, setVerification] = useState<Verification | null>(null);
  const [now, setNow] = useState(Date.now);
  const mounted = useRef(true);
  const [sessionKey] = useState(agencySessionKey);
  const action = useAccountAction(`agency-contact:${sessionKey}`);
  const serverNow = now + (verification?.serverOffset || 0);
  const retrySeconds = verification ? Math.max(0, Math.ceil((verification.retryAt - serverNow) / 1000)) : 0;
  const expiresSeconds = verification ? Math.max(0, Math.ceil((verification.expiresAt - serverNow) / 1000)) : 0;

  useEffect(function () {
    onPendingChange(action.pending);
  }, [action.pending, onPendingChange]);

  useEffect(function () {
    mounted.current = true;
    const timer = window.setInterval(function () { setNow(Date.now()); }, 1000);
    return function () { mounted.current = false; window.clearInterval(timer); };
  }, []);

  function normalize(value: string): string {
    return isEmail ? value.trim().toLowerCase() : normalizeLoginDigits(value).replace(/^(?:\+98|0098)/, "0");
  }

  function sendCode() {
    void action.run(async function () {
      if (agencySessionKey() !== sessionKey) throw new Error(t("نشست معتبر نیست."));
      let values: ContactValues;
      try { values = await form.validateFields(["recipient", "currentPassword"]); }
      catch { AppFeedback.error({ title: t("اطلاعات فرم را کامل کنید.") }); return; }
      const recipient = normalize(values.recipient);
      const result = await requestAgencyContact(recipient, values.currentPassword);
      if (!mounted.current || agencySessionKey() !== sessionKey) return;
      const timing = parseAgencyOtpTiming(result);
      if (!timing || !result.verificationId) throw new Error(t("اطلاعات زمان اعتبار کد دریافت نشد؛ دوباره تلاش کنید."));
      setVerification({ ...timing, id: result.verificationId, recipient });
      setNow(Date.now());
      form.setFieldValue("code", "");
      AppFeedback.info({ title: t("درخواست ارسال کد تأیید ثبت شد."), description: t("کد ارسال‌شده به اطلاعات تماس جدید را وارد کنید. اعتبار کد چهار دقیقه است.") });
    });
  }

  function confirm(values: ContactValues) {
    void action.run(async function () {
      if (agencySessionKey() !== sessionKey) throw new Error(t("نشست معتبر نیست."));
      if (!verification || verification.recipient !== normalize(values.recipient)
          || Date.now() + verification.serverOffset >= verification.expiresAt) {
        AppFeedback.error({ title: t("زمان کد تمام شد؛ کد جدید دریافت کنید.") });
        return;
      }
      const confirmed = await confirmAgencyContact(verification.id,
        normalizeLoginDigits(values.code || ""), verification.recipient, isEmail);
      if (!confirmed || !mounted.current) return;
      AppFeedback.success({ title: t("اطلاعات تماس با موفقیت تأیید شد."), description: t("نشست فعلی شما همچنان فعال است.") });
      onConfirmed(verification.recipient, isEmail);
    });
  }

  return (
    <AccountForm
      form={form}
      className="account-ui-form"
      layout="vertical"
      disabled={action.pending}
      onFinish={confirm}
      onFinishFailed={function () { AppFeedback.error({ title: t("اطلاعات فرم را کامل کنید.") }); }}
    >
      <p className="account-ui__description">{t("برای افزودن اطلاعات تماس، رمز فعلی و کد تأیید مقصد جدید لازم است. پس از تأیید، نشست فعلی شما حفظ می‌شود.")}</p>
      <AccountForm.Item
        name="recipient"
        label={isEmail ? t("افزودن ایمیل") : t("افزودن شماره موبایل")}
        rules={[
          { required: true, message: isEmail ? t("ایمیل را وارد کنید.") : t("شماره موبایل را وارد کنید") },
          { validator: function (_, value?: string) {
            if (!value || (isEmail ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalize(value)) : /^09\d{9}$/.test(normalize(value)))) return Promise.resolve();
            return Promise.reject(new Error(isEmail ? t("آدرس ایمیل معتبر وارد کنید.") : t("شماره همراه معتبر وارد کنید.")));
          } },
        ]}
      >
        <AccountInput
          dir="ltr"
          inputMode={isEmail ? "email" : "tel"}
          autoComplete={isEmail ? "email" : "tel"}
          maxLength={isEmail ? 254 : 20}
          onChange={function () { form.setFieldValue("code", ""); }}
        />
      </AccountForm.Item>
      <AccountForm.Item
        name="currentPassword"
        label={t("رمز فعلی")}
        rules={[{ required: true, message: t("رمز فعلی را وارد کنید.") }]}
      >
        <AccountInput.Password autoComplete="current-password" />
      </AccountForm.Item>
      {verification && (
        <>
          <p className="account-ui__description">
            {t("مقصد کد تأیید")}: <bdi dir="ltr">{verification.recipient}</bdi>
          </p>
          <AccountForm.Item
            name="code"
            label={t("کد تأیید")}
            normalize={normalizeLoginDigits}
            rules={[{ required: true, pattern: /^\d{6}$/, message: t("کد تأیید باید شش رقم باشد.") }]}
          >
            <AccountInput
              dir="ltr"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
            />
          </AccountForm.Item>
          <p className="account-ui__description">
            {expiresSeconds > 0 ? t("اعتبار کد") : t("زمان کد تمام شد؛ کد جدید دریافت کنید.")}
            {expiresSeconds > 0 && <bdi> {formatAgencyOtpCountdown(expiresSeconds)}</bdi>}
          </p>
        </>
      )}
      <div className="account-ui__actions">
        {verification && (
          <AccountButton
            type="primary"
            htmlType="submit"
            loading={action.pending}
            disabled={expiresSeconds === 0}
          >
            {t("تأیید اطلاعات تماس")}
          </AccountButton>
        )}
        <AccountButton
          onClick={sendCode}
          loading={action.pending}
          disabled={retrySeconds > 0}
        >
          {retrySeconds > 0 ? t("ارسال مجدد پس از {{seconds}} ثانیه", { seconds: retrySeconds }) : t("دریافت کد تأیید")}
        </AccountButton>
      </div>
    </AccountForm>
  );
}
